import Badge from "@mui/material/Badge"
import Box from "@mui/material/Box"
import FormHelperText from "@mui/material/FormHelperText"
import InputLabel from "@mui/material/InputLabel"
import Stack from "@mui/material/Stack"
import Tab from "@mui/material/Tab"
import Tabs from "@mui/material/Tabs"
import { useState } from "react"
import type { Control, FieldErrors } from "react-hook-form"

import TextInput from "@components/text_input"
import {
  MOBILE_RELEASE_NOTE_LOCALES,
  type MobileReleaseNoteLocale
} from "@models/mobile_app_version"

import type { MobileAppVersionFormData } from "./model"

const LOCALE_LABELS: Record<MobileReleaseNoteLocale, string> = {
  tr: "Türkçe",
  en: "English",
  ar: "العربية"
}

type ReleaseNotesTabsProps = {
  control: Control<MobileAppVersionFormData>
  errors: FieldErrors<MobileAppVersionFormData>
  translate: (key: string) => string
  disabled?: boolean
}

export default function ReleaseNotesTabs({
  control,
  errors,
  translate,
  disabled
}: ReleaseNotesTabsProps) {
  const [active, setActive] = useState<MobileReleaseNoteLocale>("tr")

  return (
    <Stack gap={1}>
      <InputLabel>{translate("mobile_app_version_release_notes")}</InputLabel>

      <Tabs
        value={active}
        onChange={(_, value) => setActive(value as MobileReleaseNoteLocale)}
        variant="fullWidth"
        sx={{ minHeight: 36 }}
      >
        {MOBILE_RELEASE_NOTE_LOCALES.map((locale) => (
          <Tab
            key={locale}
            value={locale}
            label={
              // Without this dot, a validation error on a hidden locale produces
              // a form that refuses to submit with no visible reason.
              <Badge
                color="error"
                variant="dot"
                invisible={!errors?.releaseNotes?.[locale]}
              >
                {LOCALE_LABELS[locale]}
              </Badge>
            }
          />
        ))}
      </Tabs>

      {/* Every locale stays MOUNTED and is hidden with CSS. Unmounting the
          inactive panels would hide their validation errors and make
          formState.isValid depend on which tab happens to be open. */}
      {MOBILE_RELEASE_NOTE_LOCALES.map((locale) => (
        <Box
          key={locale}
          sx={{ display: active === locale ? "block" : "none" }}
        >
          <Stack gap={1.5} sx={{ pt: 1 }}>
            <TextInput<MobileAppVersionFormData>
              name={`releaseNotes.${locale}.title`}
              control={control}
              disabled={disabled}
              label={translate("mobile_app_version_note_title")}
              placeholder={translate("mobile_app_version_note_title")}
              inputProps={{ dir: locale === "ar" ? "rtl" : "ltr" }}
            />
            <TextInput<MobileAppVersionFormData>
              name={`releaseNotes.${locale}.body`}
              control={control}
              disabled={disabled}
              multiline
              rows={5}
              label={translate("mobile_app_version_note_body")}
              placeholder={translate("mobile_app_version_note_body")}
              inputProps={{ dir: locale === "ar" ? "rtl" : "ltr" }}
            />
          </Stack>
        </Box>
      ))}

      {!!errors?.releaseNotes?.message && (
        <FormHelperText error>{errors.releaseNotes.message}</FormHelperText>
      )}
    </Stack>
  )
}
